import styled from '@emotion/styled';
import {
  CircularProgress,
  Dialog,
  DialogContent,
  Typography
} from '@material-ui/core';
import { Check } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useUnsafeTranslation } from '../../../../i18next';
import { FlexContainer, FlexContainerVertical } from '../../../../layout/Flex';

const StepRow = styled(FlexContainer)((p) => ({
  gap: p.theme.spacing(1.5)
}));

const StepIconSlot = styled('div')({
  width: 20,
  height: 20,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0
});

const DoneIcon = styled(Check)((p) => ({
  color: p.theme.custom.colors.green.w500
}));

const PendingText = styled(Typography)<{ dim: boolean }>((p) => ({
  color: p.dim ? p.theme.custom.colors.gray.w500 : 'inherit'
}));

/**
 * In-progress modal shown while `user_mergeEmployees` runs. The callable is a
 * single synchronous call, so the two stages are a visual approximation: we
 * advance from "Applying" to "Moving" after a short beat so the copy matches
 * the design while the (possibly slow) call completes.
 */
export const MergingModal = ({ open }: { open: boolean }) => {
  const { t } = useUnsafeTranslation();
  const [stage, setStage] = useState(0);

  useEffect(() => {
    if (!open) {
      setStage(0);
      return;
    }
    const timer = setTimeout(() => setStage(1), 1500);
    return () => clearTimeout(timer);
  }, [open]);

  const renderStepIcon = (index: number) => {
    if (stage > index) {
      return <DoneIcon size={18} />;
    }
    return <CircularProgress size={16} />;
  };

  return (
    <Dialog
      open={open}
      // Non-dismissable: the merge must not be interrupted.
      disableBackdropClick
      disableEscapeKeyDown
      maxWidth="xs"
      fullWidth
    >
      <DialogContent>
        <FlexContainerVertical spacing={2} style={{ padding: 8 }}>
          <CircularProgress size={28} />
          <div>
            <Typography variant="h4" component="h2">
              {t('Merging employees…')}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t('This usually takes a few seconds. Keep this tab open.')}
            </Typography>
          </div>

          <FlexContainerVertical spacing={1.5}>
            <StepRow alignItems="center">
              <StepIconSlot>{renderStepIcon(0)}</StepIconSlot>
              <PendingText variant="body2" dim={stage > 0}>
                {t('Applying the values you chose')}
              </PendingText>
            </StepRow>
            <StepRow alignItems="center">
              <StepIconSlot>{renderStepIcon(1)}</StepIconSlot>
              <PendingText variant="body2" dim={false}>
                {t('Moving experiences and moments')}
              </PendingText>
            </StepRow>
          </FlexContainerVertical>
        </FlexContainerVertical>
      </DialogContent>
    </Dialog>
  );
};
